/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Phase 6c2 — BOOT-DRAW FEED BUILDERS (the G12-G21/G31/G32 data inputs).
 *
 * In the legacy board every accessory/component FORM fires its own canvas
 * draw event while rendering at boot:
 *  - accessory forms → `draw:accessory:on:canvas`
 *      (view/base_classes/accessory_view.js drawOnCanvas :2032-2089 /
 *       drawDrainPanOnCanvas :2090-2118 — Part 6 §49.6, THE payload producer)
 *  - coil form → `draw:coil:connection`
 *      (components/coil/view/form.js:232 — rated_coil JSON + qty_stacked/
 *       duty_id/h_from_floor/mounting_angle, Part 4 §50.1 G31 feed)
 *  - evap_pack form → `draw:evap_pack:connection`
 *      (components/evap_pack/view/form.js:141-174 drawConnections — G32 feed)
 *
 * The form layer is 6e2 — these builders replay the SAME payloads from the
 * bootstrap REST data so the canvas boots at visual parity. They are pure:
 * (rows in) → (bus-args out); the §49.6 JSON-diff snapshot machinery is NOT
 * needed at boot (first draw, nothing cached) and stays with the 6e2 forms.
 *
 * Boot-time simplifications vs the live drawOnCanvas (each = the legacy
 * first-render path where `self.canvasData` is still undefined):
 *  - checkForLouverAndDamper collapses to the plane-9 test (:2066 — the
 *    blade_directions-validity arm is gated on self.canvasData?.blade_direction);
 *  - checkForLinerLight collapses to false (same gate) — the live
 *    orientation_error gate (style-2 light without orientation_id) remains;
 *  - viewport_params skips the cms list validation (:2059 — the CMS accessory
 *    doc is a 6e2 form input; a malformed JSON is still dropped). Documented
 *    deviation: an orphaned viewport model (deleted from CMS) would draw here
 *    but not in legacy — flagged for the 6e verify round.
 */

import type {
  IAhuAccessoryRow,
  IAhuAccessoryDetail,
  IAhuPositionRow,
  IAhuIntWallRow,
  IAhuCoilDetail,
  IAhuRatedCoil,
  IAhuEvapPackDetail,
} from 'models';

/** Accessory types the canvas can draw (accessoryShapesType keys, lowercased). */
export const DRAWABLE_ACCESSORY_TYPES: Record<string, string> = {
  // type → REST plural (urlRoots of accessories/<type>/model/<type>.js)
  damper: 'dampers',
  louver: 'louvers',
  opening: 'openings',
  access_door: 'access_doors',
  hood: 'hoods',
  light: 'lights',
  vfd: 'vfds',
  jbox: 'jboxes',
  disconnect: 'disconnects',
  enclosure: 'enclosures',
  adp_gage: 'adp_gages',
  dog_house: 'dog_houses',
  drain_pan: 'drain_pans',
};

/** Types whose draw payload needs the position row (all drawables except drain_pan). */
export function accessoryTypeNeedsPosition(type: string): boolean {
  return type !== 'drain_pan';
}

export interface AccessoryBootEntry {
  row: IAhuAccessoryRow;
  detail: IAhuAccessoryDetail | null;
  position: IAhuPositionRow | null;
}

/** `draw:accessory:on:canvas` positional args (§3.4 #8 / §25.2). */
export interface AccessoryDrawArgs {
  accessory: Record<string, any>;
  label: string;
  sectionId: number;
  mounting: Record<string, any>;
  viewport: Record<string, any> | null;
  int_wall: Record<string, any> | null;
}

/**
 * accessory_view.js drawOnCanvas (:2032-2089) — boot-path port. Returns null
 * when the legacy gate would not fire the event (or would fire
 * remove_accessory_from_canvas, a no-op before anything is drawn).
 */
export function buildAccessoryBootDraw(
  type: string,
  entry: AccessoryBootEntry,
  intWalls: IAhuIntWallRow[]
): AccessoryDrawArgs | null {
  const model = entry.detail as Record<string, any> | null;
  if (!model) return null;

  // drawDrainPanOnCanvas (:2090-2118): gate dim1_length, empty position arg.
  // NB all returned objects are SHALLOW CLONES: the legacy draw paths mutate
  // their inputs (drawAccessory mounting.y/prev_y :1329-1330, the
  // `accessory.name` stamp tunnel.js:254, drawDrainPanComplete's label
  // rewrite :2876) and the RTKQ cache rows are frozen.
  if (type === 'drain_pan') {
    if (!model.dim1_length) return null;
    return {
      accessory: { ...model },
      label: type,
      sectionId: Number(model.section_id ?? entry.row.section_id),
      mounting: {},
      viewport: null,
      int_wall: null,
    };
  }

  const position = entry.position as Record<string, any> | null;
  const mounting_plane = position?.mounting_plane_id;
  const height = model.height || model.overall_depth || model.height_calc;
  const width = model.width || model.overall_width || model.width_calc;

  // checkForLouverAndDamper boot arm (:2066): fan_intake plane 9 → removed.
  if (mounting_plane == 9) return null;
  // Common DRAW gate (:2079/:2084).
  if (!mounting_plane || !height || !width) return null;

  const light_params = model.light_params ? safeParse(model.light_params) : null;
  const isLinearLight = type === 'light' && light_params?.style_id == 2;

  // blade_error (:2075) — damper/louver labels without a blade direction.
  const label = String(model.label || '');
  const blade_error =
    label.startsWith('Damper') || label.startsWith('Louver') ? !model.blade_direction_id : false;
  if (blade_error) return null;
  // orientation_error (:2076) — linear light without orientation.
  if (isLinearLight && !model.orientation_id) return null;
  // checkForHoodStyle4NoHeight2 (:2065).
  if (type === 'hood' && model.style_id == 4 && model.height_2 == null) return null;
  // Hood needs ≥1 angle (:2079).
  if (
    type === 'hood' &&
    !(model.angle_1 || model.angle_2 || model.angle_3 || model.angle_4)
  )
    return null;
  // VFD needs a master (:2084).
  if (type === 'vfd' && !model.master_vfd_id) return null;
  // has-vfd-fix — check_vfd mirrors the fan's "Has VFD" checkbox
  // (AHUAccessoryVFD.check_vfd); skip_3d_model is Annexair's VFD-in-motor-
  // housing case (AHUAccessoryVFD.skip_3d_model). Neither should draw a box
  // at boot, matching the FreeCAD generator's ahu.xml gate.
  if (type === 'vfd' && (!model.check_vfd || model.skip_3d_model)) return null;

  // viewport (:2084 — parsed viewport_params JSON). Legacy gates the parsed
  // params against the CMS viewport catalog (accessory_view.js:2058 — no model
  // match → null). Doors without a selected viewport store {"shape_id":"1"}
  // (no size/model): passing that through crashes createViewport on
  // `.size.split('x')` and white-screened ~18% of real AHUs (6c2 verify
  // finding 1). Gate on the fields the draw actually consumes.
  const viewportRaw = model.viewport_params ? safeParse(model.viewport_params) : null;
  const viewport =
    viewportRaw && (viewportRaw as { size?: string }).size && (viewportRaw as { model?: unknown }).model
      ? viewportRaw
      : null;

  // int_wall (:2059): plane_id > 6 = wall id, resolved in the section's walls.
  const int_wall =
    mounting_plane > 6 ? intWalls.find((w) => w.id === mounting_plane) ?? null : null;
  // Wall-mounted accessory whose wall row vanished → legacy removes (:2066 first clause
  // is canvasData-gated at boot, but a draw against a missing wall id would
  // also never resolve a parent) — skip.
  if (mounting_plane > 6 && !int_wall) return null;

  return {
    accessory: { ...model },
    label: `${type}${isLinearLight ? '_LINEAR' : ''}`,
    sectionId: Number(model.section_id ?? entry.row.section_id),
    mounting: { ...(position as Record<string, any>) },
    viewport,
    int_wall: int_wall ? { ...(int_wall as Record<string, any>) } : null,
  };
}

/** `draw:coil:connection` positional args (coil form.js:232). */
export interface CoilDrawArgs {
  ratedCoil: Record<string, any>;
  sectionId: number;
  qtyStacked: number;
  dutyId: any;
  hFromFloor: any;
  mountingAngle: any;
}

export function buildCoilBootDraw(coil: IAhuCoilDetail, rated: IAhuRatedCoil | null): CoilDrawArgs | null {
  // drawCoilConnections bails without coil.model (canvas.view3d:5078) — skip
  // the event like an unrated legacy coil never fires a visible draw.
  if (!rated || !rated.model || coil.section_id == null) return null;
  return {
    // Cloned — the canvas caches the object (coilConnections registry).
    ratedCoil: { ...(rated as Record<string, any>) },
    sectionId: Number(coil.section_id),
    qtyStacked: Number(coil.qty_stacked) || 1,
    dutyId: coil.duty_id,
    hFromFloor: (coil as Record<string, any>).h_from_floor,
    mountingAngle: (coil as Record<string, any>).mounting_angle,
  };
}

/** `draw:evap_pack:connection` data payload (evap_pack form.js:141-174 drawConnections). */
export function buildEvapBootDraw(evap: IAhuEvapPackDetail): Record<string, any> | null {
  const model = evap as Record<string, any>;
  // The legacy reads model.cms_fill/.cms_drain/.cms_overflow unconditionally —
  // they are server-joined blocks; absent (mis-selected evap) the form throws
  // before triggering. Gate instead (defensive, same observable: no draw).
  if (!model.cms_fill || !model.cms_drain || !model.cms_overflow) return null;
  return {
    fill: {
      conn_hand: model.fill_conn_hand_id,
      pipe_nipple_od_dia: model.fill_nipple_od_dia,
      pipe_nipple_length: model.fill_pipe_nipple_length,
      pipe_nipple_protrusion: model.cms_fill.pipe_nipple_protrusion,
      conn_cl_ups_edge_sump: model.cms_fill.conn_cl_ups_edge_sump,
      conn_cl_h_sump_floor: model.cms_fill.conn_cl_h_sump_floor,
    },
    drain: {
      conn_hand: model.drain_conn_hand_id,
      pipe_nipple_od_dia: model.drain_nipple_od_dia,
      pipe_nipple_length: model.drain_pipe_nipple_length,
      pipe_nipple_protrusion: model.cms_drain.pipe_nipple_protrusion,
      conn_cl_ups_edge_sump: model.cms_drain.conn_cl_ups_edge_sump,
      conn_cl_h_sump_floor: model.cms_drain.conn_cl_h_sump_floor,
    },
    overflow: {
      conn_hand: model.overflow_conn_hand_id,
      pipe_nipple_od_dia: model.overflow_nipple_od_dia,
      pipe_nipple_length: model.overflow_pipe_nipple_length,
      pipe_nipple_protrusion: model.cms_overflow.pipe_nipple_protrusion,
      conn_cl_ups_edge_sump: model.cms_overflow.conn_cl_ups_edge_sump,
      conn_cl_h_sump_floor: model.cms_overflow.conn_cl_h_sump_floor,
    },
  };
}

/** The assembled boot-draw bundle AhuCanvas replays after the first render pass. */
export interface CanvasBootDraws {
  /** G11 — draw:int_wall:on:canvas rows (walls FIRST: accessories parent into them). */
  intWallDraws: IAhuIntWallRow[];
  /** G12-G21 — draw:accessory:on:canvas payloads. */
  accessoryDraws: AccessoryDrawArgs[];
  /** G31 — draw:coil:connection payloads. */
  coilDraws: CoilDrawArgs[];
  /** G32 — draw:evap_pack:connection payloads. */
  evapDraws: Array<{ sectionId: number; data: Record<string, any> }>;
}

function safeParse(value: any): any {
  if (value == null) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}
